import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadBlob } from '@/utils/download';
import { DEFAULT_DASHBOARD_PAGE_SIZE } from '../constants/dashboard.constants';
import {
  dcrDashboardResponseSchema,
  derDashboardResponseSchema,
  systemDashboardResponseSchema,
  workSummarySchema,
  type DcrDashboardResponse,
  type DcrDashboardSearch,
  type DerDashboardResponse,
  type DerDashboardSearch,
  type SystemDashboardResponse,
  type SystemDashboardSearch,
  type WorkSummary,
} from '../types/dashboard.types';

export const dashboardQueryKeys = {
  all: () => ['dashboards'] as const,
  system: (params: SystemDashboardSearch) =>
    [...dashboardQueryKeys.all(), 'system', params] as const,
  dcr: (params: DcrDashboardSearch) => [...dashboardQueryKeys.all(), 'dcr', params] as const,
  der: (params: DerDashboardSearch) => [...dashboardQueryKeys.all(), 'der', params] as const,
  workSummary: (userId: number, dateFrom?: string, dateTo?: string, dcrTypeId?: number) =>
    [...dashboardQueryKeys.all(), 'work-summary', userId, dateFrom, dateTo, dcrTypeId] as const,
};

export const systemDashboardInfiniteQueryOptions = (params: SystemDashboardSearch) =>
  infiniteQueryOptions({
    queryKey: dashboardQueryKeys.system(params),
    queryFn: async ({ pageParam }): Promise<SystemDashboardResponse> => {
      const res = await apiClient.get(API.dashboard.system, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_DASHBOARD_PAGE_SIZE },
      });
      return systemDashboardResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const dcrDashboardInfiniteQueryOptions = (params: DcrDashboardSearch) =>
  infiniteQueryOptions({
    queryKey: dashboardQueryKeys.dcr(params),
    queryFn: async ({ pageParam }): Promise<DcrDashboardResponse> => {
      const res = await apiClient.get(API.dashboard.dcr, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_DASHBOARD_PAGE_SIZE },
      });
      return dcrDashboardResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const derDashboardInfiniteQueryOptions = (params: DerDashboardSearch) =>
  infiniteQueryOptions({
    queryKey: dashboardQueryKeys.der(params),
    queryFn: async ({ pageParam }): Promise<DerDashboardResponse> => {
      const res = await apiClient.get(API.dashboard.der, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_DASHBOARD_PAGE_SIZE },
      });
      return derDashboardResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const workSummaryQueryOptions = (
  userId: number | null,
  period: {
    dateFrom?: string | undefined;
    dateTo?: string | undefined;
    dcrTypeId?: number | undefined;
  },
) =>
  queryOptions({
    queryKey: dashboardQueryKeys.workSummary(
      userId ?? 0,
      period.dateFrom,
      period.dateTo,
      period.dcrTypeId,
    ),
    queryFn: async (): Promise<WorkSummary> => {
      const res = await apiClient.get(API.dashboard.workSummary(userId ?? 0), {
        params: period,
      });
      return workSummarySchema.parse(res.data);
    },
    enabled: userId !== null,
    staleTime: STALE_TIME,
  });

async function download(url: string, params: object, name: string): Promise<void> {
  const res = await apiClient.get<Blob>(url, { params, responseType: 'blob' });
  downloadBlob(res.data, name);
}

export const downloadSystemDashboard = (params: SystemDashboardSearch): Promise<void> =>
  download(API.dashboard.systemExport, params, 'system-dashboard.xlsx');

export const downloadDerDashboard = (params: DerDashboardSearch): Promise<void> =>
  download(API.dashboard.derExport, params, 'der-dashboard.xlsx');
